import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  first_name: string;
  last_name: string;
  dob: string;
  phone_number: string;
  email: string;
  family_members: any[]; // Always initialized as array
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoggedIn: boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser({
          ...parsedUser,
          family_members: parsedUser.family_members || [],
        });
        setToken(storedToken);
      } catch (e) {
        console.error("Failed to parse stored user data:", e);
        localStorage.removeItem('user');
      }
    }
  }, []);

  const login = (userData: User, authToken: string) => {
    const safeUserData: User = {
      ...userData,
      family_members: userData.family_members || [],
    };
    setUser(safeUserData);
    setToken(authToken);
    localStorage.setItem('accessToken', authToken);
    localStorage.setItem('user', JSON.stringify(safeUserData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider
      value={{ user, token, isLoggedIn: !!user && !!token, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
